// Normalton Square on screen: every prop placed from shared/map.ts LAYOUT (id + kind + transform), meshes from
// client/assets/town/ (mesh-forge, manifest.json). The town owns the things that move without a body: the bus, the
// parade float, capsules on the stalls, the fountain water, and the bandstand where the Town Crier stands.

import * as THREE from "three";
import { LAYOUT, STALLS, BUS, FOUNTAIN, CRIER, type Placement } from "../../../shared/map";
import { glb, optional } from "./assets";
import { gradientMap, glowify, type Tier } from "./core";

export interface TownManifest { town?: Record<string, { file: string; variants?: number; unit?: boolean; outline?: boolean }> }
const OUTLINE_K = new THREE.Color().setRGB(0.336, 0.315, 0.378);

/** convert every material in a loaded scene to the house toon material (+ optional outline twins) */
export function toonify(root: THREE.Object3D, tier: Tier, outline: boolean) {
  const meshes: THREE.Mesh[] = [];
  root.traverse((o: any) => { if (o.isMesh && !/_outline$/.test(o.name)) meshes.push(o); });
  for (const m of meshes) {
    const conv = (src: any) => {
      if (src.isMeshToonMaterial) return src;
      const t = new THREE.MeshToonMaterial({ gradientMap, map: src.map ?? null, color: src.color ?? 0xffffff, vertexColors: !!src.vertexColors, name: src.name, transparent: !!src.transparent, opacity: src.opacity ?? 1 });
      if (src.map) src.map.colorSpace = THREE.SRGBColorSpace;
      const glow = src.userData?.glow ?? 0;
      if (glow > 0) glowify(t, glow);
      return t;
    };
    m.material = Array.isArray(m.material) ? m.material.map(conv) : conv(m.material);
    m.castShadow = tier !== "low" && !/ground|paving|road|water|decal/i.test(m.name);
    m.receiveShadow = tier !== "low";
    if (outline && tier !== "low" && !/ground|paving|road|water|decal/i.test(m.name)) {
      const src = (Array.isArray(m.material) ? m.material[0] : m.material) as any;
      const vc = !!src.vertexColors;
      const om = new THREE.MeshBasicMaterial({ map: src.map ?? null, vertexColors: vc, color: src.map || vc ? OUTLINE_K : new THREE.Color(src.color ?? 0xffffff).multiply(OUTLINE_K), side: THREE.BackSide });
      const has = !!m.geometry.getAttribute("_outlinenormal");
      om.onBeforeCompile = (sh) => {
        sh.vertexShader = (has ? "attribute vec3 _outlinenormal;\n" : "") + sh.vertexShader.replace("#include <begin_vertex>", `#include <begin_vertex>\n transformed += ${has ? "_outlinenormal" : "normalize(normal)"} * 0.014;`);
      };
      om.customProgramCacheKey = () => `tol${has ? 1 : 0}${vc ? "v" : ""}`;
      const twin = new THREE.Mesh(m.geometry, om); twin.name = m.name + "_outline";
      twin.position.copy(m.position); twin.quaternion.copy(m.quaternion); twin.scale.copy(m.scale);
      m.parent?.add(twin);
    }
  }
}

/** a static prop: compute its matrices once and take it out of the per-frame matrix walk */
function freeze(o: THREE.Object3D) {
  o.updateMatrixWorld(true);
  o.traverse((c) => { c.matrixAutoUpdate = false; c.matrixWorldAutoUpdate = false; });
}

/** the floating stall capsule's size against the counter-sized model (0.26 m → ~0.75 m) */
const CAP_SIGN_SCALE = 2.9;
const SIGN_GLOW = 0.18;

export class Town {
  group = new THREE.Group();
  stallCaps: (THREE.Object3D | null)[] = STALLS.map(() => null);
  private capsuleTpl: Record<string, THREE.Object3D> = {};
  bus: THREE.Object3D | null = null;
  float: THREE.Object3D | null = null;
  floatCapsule: THREE.Object3D | null = null;
  private water: THREE.Object3D | null = null;
  roof: THREE.Object3D | null = null;
  private t = 0;
  /** each stall's awning top (m): a stocked stall shows its capsule floating above it, where the play camera sees it */
  private stallTop: number[] = [];
  private signTpl: THREE.Object3D | null = null;
  ready: Promise<void>;

  constructor(private man: TownManifest, private tier: Tier) {
    this.ready = this.build();
  }

  /** manifest layout_files: stall_<v>, tree_<v>, facade_<v>, flowerbed_v<v>_<w>x<d> (JS String(n)), else <kind>.glb */
  private file(kind: string, variant?: number, w?: number, d?: number): string {
    if (kind === "flowerbed") return `./assets/town/flowerbed_v${variant ?? 0}_${String(w)}x${String(d)}.glb`;
    if ((kind === "stall" || kind === "tree" || kind === "facade") && variant !== undefined) return `./assets/town/${kind}_${variant}.glb`;
    const e = this.man.town?.[kind];
    return "./assets/" + (e?.file ?? `town/${kind}.glb`);
  }

  private async place(p: Placement) {
    const g = await optional(glb(this.file(p.kind, p.variant, p.w, p.d)));
    if (!g) return;
    const o = g.scene.clone(true);
    toonify(o, this.tier, this.man.town?.[p.kind]?.outline !== false);
    o.position.set(p.x, 0, p.z); o.rotation.y = p.rot;
    if (p.kind !== "fountain" && p.kind !== "bandstand") freeze(o);   // static: matrices once, never re-walked per frame
    if (p.kind === "fountain") o.traverse((m: any) => { if (/water/i.test(m.name)) this.water = m; });
    if (p.kind === "bandstand") o.traverse((m: any) => { if (m.name === "roof") this.roof = m; });
    if (p.kind === "stall" && p.variant !== undefined) { o.updateMatrixWorld(true); this.stallTop[p.variant] = new THREE.Box3().setFromObject(o).max.y; }
    this.group.add(o);
  }

  private async build() {
    await Promise.all(LAYOUT.map((p) => this.place(p)));
    for (const r of ["common", "rare", "epic", "legendary", "golden"]) {
      const g = await optional(glb(`./assets/town/capsule_${r}.glb`));
      if (g) { const o = g.scene; toonify(o, this.tier, true); o.traverse((m: any) => { if (m.isMesh) m.layers.enable(1); }); this.capsuleTpl[r] = o; }
    }
    // the stall sign: the common capsule with its glow turned down — in daylight at sign size full glow blows it out to a
    // flat white blob; the sign must read as the HUD's capsule icon (cream top, gold band, lavender base)
    const sg = await optional(glb(`./assets/town/capsule_common.glb`));
    if (sg) {
      sg.scene.traverse((m: any) => { if (m.isMesh) for (const mt of [m.material].flat()) if (mt.userData?.glow) mt.userData.glow = Math.min(mt.userData.glow, SIGN_GLOW); });
      toonify(sg.scene, this.tier, true); this.signTpl = sg.scene;
    }
    const bus = await optional(glb(this.file("bus")));
    if (bus) { this.bus = bus.scene.clone(true); toonify(this.bus, this.tier, true); this.bus.visible = false; this.group.add(this.bus); }
    const fl = await optional(glb(this.file("parade_float")));
    if (fl) { this.float = fl.scene.clone(true); toonify(this.float, this.tier, true); this.float.visible = false; this.group.add(this.float); }
  }

  signY(i: number) { return this.capY(i); }
  private capY(i: number) { return (this.stallTop[i] ?? 2.6) + 0.55; }

  capsuleMesh(r: string): THREE.Object3D | null { const t = this.capsuleTpl[r] ?? this.capsuleTpl.common; return t ? t.clone(true) : null; }

  /** the stalls' capsules from the snapshot (true = a capsule sits there) */
  setStalls(has: boolean[]) {
    has.forEach((on, i) => {
      if (on && !this.stallCaps[i]) {
        const c = this.signTpl ? this.signTpl.clone(true) : this.capsuleMesh("common"); if (!c) return;
        // 2026-10-05 owner: "I walk around and see no capsules" — a 0.26 m capsule on the counter hides under the awning
        // from the 55° camera. The stall now shows it big, floating over the awning (the stall's sign: "capsule here").
        const s = STALLS[i]; c.scale.multiplyScalar(CAP_SIGN_SCALE); c.position.set(s.capsule.x, this.capY(i), s.capsule.z); c.rotation.y = s.rot;
        this.group.add(c); this.stallCaps[i] = c;
      } else if (!on && this.stallCaps[i]) { this.stallCaps[i]!.removeFromParent(); this.stallCaps[i] = null; }
    });
  }

  /** bus: drives along the road, docks with its door at BUS.door */
  setBus(state: string, age: number) {
    if (!this.bus) return;
    const drive = 2.0, x0 = BUS.from, x1 = BUS.to, stop = BUS.stopX;
    const ease = (t: number) => 1 - Math.pow(1 - Math.min(1, Math.max(0, t)), 3);
    let x = x0, vis = true;
    if (state === "away") vis = false;
    else if (state === "arriving") x = x0 + (stop - x0) * ease(age / drive);
    else if (state === "docked") x = stop;
    else if (state === "leaving") x = stop + (x1 - stop) * Math.pow(Math.min(1, age / drive), 2);
    this.bus.visible = vis; this.bus.position.set(x, 0, BUS.road); this.bus.rotation.y = Math.PI / 2;
  }

  setFloat(f: { x: number; z: number; rot: number } | null, golden: number) {
    if (!this.float) return;
    this.float.visible = !!f;
    if (f) { this.float.position.set(f.x, 0, f.z); this.float.rotation.y = f.rot; }
    // the Golden Capsule rides the float's pedestal while golden === 1 (on the float)
    if (golden === 1 && f) {
      if (!this.floatCapsule) { this.floatCapsule = this.capsuleMesh("golden"); if (this.floatCapsule) this.group.add(this.floatCapsule); }
      if (this.floatCapsule) {
        const lz = -0.35, c = Math.cos(f.rot), s = Math.sin(f.rot);
        this.floatCapsule.position.set(f.x + lz * s, 1.89 + 0.04 + Math.sin(this.t * 3) * 0.05, f.z + lz * c);
        this.floatCapsule.rotation.y += 0.03;
      }
    } else if (this.floatCapsule) { this.floatCapsule.removeFromParent(); this.floatCapsule = null; }
  }

  update(dt: number) {
    this.t += dt;
    this.stallCaps.forEach((c, i) => { if (c) { c.rotation.y += dt * 1.6; c.position.y = this.capY(i) + Math.sin(this.t * 2.4 + i) * 0.12; } });
    if (this.water) this.water.position.y = Math.sin(this.t * 1.3) * 0.008;   // a gentle lap
  }

  static crierSpot() { return CRIER; }
  static fountain() { return FOUNTAIN; }
}
